import React from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';

const Hero = ({ onReserveClick }) => {
  const scrollToNext = () => {
    const nextSection = document.getElementById('about');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="inicio" className="relative w-full h-screen overflow-hidden">
      {/* Background Image with Ken Burns Effect */}
      <div className="absolute inset-0 z-0">
        <motion.img
          src="/images/hotel.webp"
          alt="Sumaq Mayu Casa Hotel Eco Lodge"
          className="w-full h-full object-cover object-center"
          initial={{ scale: 1 }}
          animate={{ scale: 1.15 }}
          transition={{
            duration: 20,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
        {/* Rich Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.55) 40%, rgba(10, 25, 15, 0.55) 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center px-4 text-center text-white">
        <div className="max-w-3xl">
          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.5 }}
            className="text-sm uppercase tracking-[0.3em] mb-4 font-light"
            style={{ textShadow: '0 2px 15px rgba(0, 0, 0, 0.8), 0 0 30px rgba(0, 0, 0, 0.4)' }}
          >
            Casa Hotel en la Selva Amazónica
          </motion.p>

          {/* Decorative Line Separator */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 2.8, ease: 'easeOut' }}
            className="mx-auto mb-6"
            style={{
              width: '60px',
              height: '2px',
              background: '#c4ad79',
              transformOrigin: 'center',
              borderRadius: '2px',
            }}
          />

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.7 }}
            className="text-5xl sm:text-6xl md:text-8xl font-bold mb-6 leading-tight"
            style={{
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 25px rgba(0, 0, 0, 0.8), 0 0 40px rgba(0, 0, 0, 0.4)',
            }}
          >
            Sumaq Mayu
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.9 }}
            className="text-base sm:text-lg md:text-xl mb-12 max-w-xl mx-auto leading-relaxed font-light"
            style={{ textShadow: '0 2px 15px rgba(0, 0, 0, 0.8), 0 0 30px rgba(0, 0, 0, 0.4)' }}
          >
            Descubre la magia de la selva en un refugio de lujo sostenible. Una experiencia inolvidable te espera.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 3.1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            {/* Primary Button - Teal */}
            <motion.button
              onClick={onReserveClick}
              whileHover={{
                scale: 1.05,
                boxShadow: '0 15px 35px rgba(45, 212, 191, 0.3)',
              }}
              whileTap={{ scale: 0.97 }}
              className="px-10 py-3.5 text-sm font-semibold uppercase tracking-wider rounded-full border-none cursor-pointer transition-all duration-300"
              style={{
                background: '#14B8A6',
                color: '#ffffff',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}
            >
              Reservar Ahora
            </motion.button>

            {/* Secondary Button - Glass */}
            <motion.button
              onClick={scrollToNext}
              whileHover={{
                scale: 1.05,
                borderColor: 'rgba(255, 255, 255, 0.6)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
              whileTap={{ scale: 0.97 }}
              className="px-10 py-3.5 text-sm font-semibold uppercase tracking-wider rounded-full cursor-pointer transition-all duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: '#ffffff',
                border: '1.5px solid rgba(255, 255, 255, 0.3)',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}
            >
              Descubre Más
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Down Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 3.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <button
          onClick={scrollToNext}
          className="border-none text-white cursor-pointer"
          aria-label="Scroll to next section"
          style={{ background: 'none' }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4))' }}
          >
            <FaChevronDown size={32} />
          </motion.div>
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
